import {useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';

import {usePromotions} from '@salesforce/commerce-sdk-react';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {useCheckoutCartInventory} from '@amblique/king-living/app/hooks/use-checkout-cart-inventory';
import {useViewCartOnFlyoutOpen} from '@amblique/king-living/app/hooks/use-view-cart-on-flyout-open';
import CartFlyoutEmptyCart from '@amblique/king-living/app/components/cart-flyout/cart-flyout-empty-cart';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {BrandLogo} from '@amblique/king-living/app/components/icons';
import {collectBasketCatalogProductIds, groupComplexLineItems} from '@amblique/king-living/app/utils/cart-utils';
import {
  lockBodyScrollFreeze,
  unlockBodyScrollFreeze,
} from '@amblique/king-living/app/utils/utils';
import {useCartFlyoutContext} from '@amblique/king-living/app/contexts/cart-flyout-context';
import {useEscapeToClose} from '@amblique/king-living/app/hooks/use-escape-to-close';
import {useOverlayInitialFocus} from '@amblique/king-living/app/hooks/use-overlay-initial-focus';
import {useOptimisticCartMutations} from '@amblique/king-living/app/hooks/use-optimistic-cart-mutations';
import {CartFlyoutCloseIcon} from '@amblique/king-living/app/components/cart-flyout/cart-flyout-close-icon';
import CartItemList from '@amblique/king-living/app/components/cart-flyout/cart-item-list';
import CartPricingSummary from '@amblique/king-living/app/components/cart-flyout/cart-pricing-summary';

// Chakra Drawer theme adds default px on header/body/footer — override explicitly.
const drawerSectionResetSx = {
  padding: '0 !important',
};

const drawerHeaderSx = {
  paddingBottom: '0 !important',
  paddingLeft: {base: '20px !important', lg: '24px !important'},
  paddingRight: {base: '20px !important', lg: '24px !important'},
  paddingTop: '0 !important',
};
// Hide the flyout on checkout unless opened via openEmptyCartOnCheckout (last item removed).
// /cart is intentionally NOT excluded — clicking the cart icon opens the flyout on all pages.
// Match main checkout only (with optional site/locale prefix), not /checkout/confirmation.
const isCheckoutPath = (pathname) => /\/checkout$/.test(pathname ?? '');

/**
 * Cart Flyout
 * Thin container: reads context, fetches products, delegates rendering to child components.
 */
const CartFlyout = () => {
  const {allowOnCheckout, isOpen: isOpenFromContext, onClose, openEmptyCartOnCheckout} =
    useCartFlyoutContext();
  const navigate = useNavigation();
  const {pathname} = useLocation();
  const onCheckout = isCheckoutPath(pathname);

  const intl = useIntl();
  const styles = useMultiStyleConfig('CartFlyout', {});
  const drawerSize = useBreakpointValue({base: 'full', lg: '480px'});
  const {data: basket, isLoading: isBasketLoading} = useCurrentBasket();

  // Group complex line items — children are hidden, prices rolled up to parent.
  // Empty-basket checks must use visible count (same as checkout order summary),
  // not raw `productItems.length` — orphan hidden child lines can remain in SCAPI
  // while the storefront shows zero products.
  const {visibleItems} = useMemo(
    () => groupComplexLineItems(basket?.productItems),
    [basket?.productItems]
  );

  const visibleItemsCount = visibleItems.length;
  const isBasketSettled = Boolean(basket?.basketId) && !isBasketLoading;
  const isCheckoutEmptyBasket =
    onCheckout && isBasketSettled && visibleItemsCount === 0;
  // Empty checkout basket must show the locked flyout even before context
  // `allowOnCheckout` / disclosure state sync (avoids open/close race).
  const isOpen =
    isCheckoutEmptyBasket ||
    (isOpenFromContext && (!onCheckout || allowOnCheckout));

  useEffect(() => {
    if (allowOnCheckout && !onCheckout) {
      onClose();
    }
  }, [allowOnCheckout, onCheckout, onClose]);

  useEffect(() => {
    if (allowOnCheckout && isBasketSettled && visibleItemsCount > 0) {
      onClose();
    }
  }, [allowOnCheckout, isBasketSettled, visibleItemsCount, onClose]);

  // Keep context flags in sync for close-when-items-return and exit CTAs.
  useEffect(() => {
    if (isCheckoutEmptyBasket && !allowOnCheckout) {
      openEmptyCartOnCheckout();
    }
  }, [isCheckoutEmptyBasket, allowOnCheckout, openEmptyCartOnCheckout]);
  const currency = basket?.currency ?? 'AUD';
  const {data: customer} = useCurrentCustomer();
  const {isRegistered} = customer || {};

  useViewCartOnFlyoutOpen({basket, currency, customer, isBasketLoading, isOpen});

  const totalItems = useMemo(
    () => visibleItems.reduce((acc, item) => acc + item.quantity, 0),
    [visibleItems]
  );

  const {outOfStockItemIds} = useCheckoutCartInventory(basket, {enabled: isOpen});

  // Fetch line SKUs plus master products (for flyout name / shortDescription only).
  const productIds = useMemo(
    () => collectBasketCatalogProductIds(basket?.productItems ?? []).join(','),
    [basket?.productItems]
  );

  const {data: productsByProductId, isLoading: isProductsLoading} = useProducts(
    {
      parameters: {ids: productIds, allImages: true, perPricebook: true},
    },
    {
      enabled: Boolean(productIds),
      select: (result) => result?.data?.reduce((acc, item) => {
        acc[item.id] = item;

        return acc;
      }, {}),
    }
  );

  // Map itemId → product (bundle support: multiple items may share a productId)
  const productsByItemId = useMemo(() => {
    if (!productsByProductId || !basket?.productItems) return {};

    return basket.productItems.reduce((acc, productItem) => {
      const product = productsByProductId[productItem.productId];

      if (product) acc[productItem.itemId] = product;

      return acc;
    }, {});
  }, [productsByProductId, basket?.productItems]);

  const {removeItem, removingItemIds, updateItemQuantity, updatingItemIds, localQuantities} =
    useOptimisticCartMutations();

  // Apply optimistic quantity overrides to visible items so the UI reflects local changes
  // instantly without waiting for the debounced SCAPI call to complete.
  const itemsWithLocalQty = useMemo(
    () => visibleItems.map((item) => {
      const override = localQuantities[item.itemId];

      return override !== undefined ? {...item, quantity: override} : item;
    }),
    [visibleItems, localQuantities]
  );

  // Catalog-level savings computed server-side in dw.order.calculate hook
  // (merchandizeTotalPrice − adjustedMerchandizeTotalPrice).
  const totalSavings = basket?.c_savingTotal ?? 0;

  // Fetch promotion details (calloutMsg) for all basket-level price adjustments.
  // Single bulk call — individual CartItems look up their own promo IDs from this map.
  const allPromoIds = useMemo(() => {
    const ids = new Set();

    (basket?.productItems ?? []).forEach((item) => {
      (item.priceAdjustments ?? []).forEach((adj) => {
        if (adj.promotionId) ids.add(adj.promotionId);
      });
    });

    return [...ids];
  }, [basket?.productItems]);

  const {data: promotionsRes} = usePromotions(
    {parameters: {ids: allPromoIds.join(',')}},
    {enabled: allPromoIds.length > 0}
  );

  const promotionsById = useMemo(() => {
    if (!promotionsRes?.data) return {};

    return promotionsRes.data.reduce((acc, promo) => {
      acc[promo.id] = promo;

      return acc;
    }, {});
  }, [promotionsRes]);

  // Body scroll lock — use the project-standard freeze utility (position:fixed approach)
  // to prevent window.scrollY jumping in nested flex+overflow-y layouts.
  // useLayoutEffect fires before paint so the lock is applied synchronously with the open.
  const savedScrollYRef = useRef(null);
  const drawerContentRef = useRef(null);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;

    savedScrollYRef.current = window.scrollY ?? document.documentElement.scrollTop ?? 0;
    lockBodyScrollFreeze(savedScrollYRef.current);

    return () => {
      unlockBodyScrollFreeze();
      // Restore scroll position so the page stays where the user left it
      if (typeof savedScrollYRef.current === 'number') {
        window.scrollTo(0, savedScrollYRef.current);
        savedScrollYRef.current = null;
      }
    };
  }, [isOpen]);

  const isLockedCheckoutEmptyFlyout = isCheckoutEmptyBasket;

  useOverlayInitialFocus({
    containerRef: drawerContentRef,
    isOpen,
    preferHeading: isLockedCheckoutEmptyFlyout,
  });

  const handleClose = useCallback(() => {
    if (isLockedCheckoutEmptyFlyout) return;
    onClose();
  }, [isLockedCheckoutEmptyFlyout, onClose]);

  useEscapeToClose(isOpen && !isLockedCheckoutEmptyFlyout, handleClose);

  const handleEditItem = useCallback(
    (url) => {
      if (url) navigate(url);
      onClose();
    },
    [navigate, onClose]
  );

  const cartTitle = intl.formatMessage({
    defaultMessage: 'Your cart',
    id: 'cart_flyout.title.your_cart',
  });

  const closeCartLabel = intl.formatMessage({
    defaultMessage: 'Close cart',
    id: 'cart_flyout.close.aria_label',
  });

  const itemCountText = intl.formatMessage(
    {
      defaultMessage: '{itemCount, plural, =0 {0 items} one {# item} other {# items}}',
      id: 'cart_flyout.title.item_count',
    },
    {itemCount: totalItems}
  );

  return (
    <Drawer
      blockScrollOnMount={false}
      closeOnEsc={!isLockedCheckoutEmptyFlyout}
      closeOnOverlayClick={!isLockedCheckoutEmptyFlyout}
      isOpen={isOpen}
      placement="right"
      returnFocusOnClose={!onCheckout}
      size={drawerSize}
      onClose={handleClose}
    >
      <DrawerOverlay className="cart-flyout__overlay" {...styles.overlay} />
      <DrawerContent ref={drawerContentRef} className="cart-flyout__content" {...styles.content}>
        {/* Header — Chakra Drawer theme adds default px; force reset after spread */}
        <Box className="cart-flyout__header-background" {...styles.headerBackground} >
          <DrawerHeader className="cart-flyout__header" {...styles.header} sx={drawerHeaderSx}>
            {!isLockedCheckoutEmptyFlyout && (
              <Flex className="cart-flyout__header-close-bar" {...styles.headerCloseBar}>
                <Box
                  aria-label={closeCartLabel}
                  as="button"
                  className="cart-flyout__close-btn"
                  type="button"
                  {...styles.closeButton}
                  onClick={handleClose}
                >
                  <CartFlyoutCloseIcon />
                </Box>
              </Flex>
            )}

            <Flex
              className="cart-flyout__header-title-row"
              {...styles.headerTitleRow}
              {...(isLockedCheckoutEmptyFlyout ? {paddingTop: {lg: '48px'}} : {})}
            >
              <Heading as="h2" className="cart-flyout__title" {...styles.title}>
                {cartTitle}
              </Heading>
              <Text className="cart-flyout__item-count" {...styles.itemCount}>
                {itemCountText}
              </Text>
            </Flex>

            <Flex className="cart-flyout__header-mobile-top" {...styles.headerMobileTop}>
              <Box className="cart-flyout__logo" {...styles.logo} sx={styles.logoSvg}>
                <BrandLogo />
              </Box>
              {!isLockedCheckoutEmptyFlyout && (
                <Box
                  aria-label={closeCartLabel}
                  as="button"
                  className="cart-flyout__close-btn"
                  type="button"
                  {...styles.closeButton}
                  onClick={handleClose}
                >
                  <CartFlyoutCloseIcon />
                </Box>
              )}
            </Flex>

            <Flex className="cart-flyout__header-mobile-title-row" {...styles.headerMobileTitleRow}>
              <Heading as="h2" className="cart-flyout__title" {...styles.title}>
                {cartTitle}
              </Heading>
              <Text className="cart-flyout__item-count" {...styles.itemCount}>
                {itemCountText}
              </Text>
            </Flex>
          </DrawerHeader>

          {/* Body — full-bleed white; list re-applies horizontal gutter */}
          <DrawerBody className="cart-flyout__body" {...styles.body} sx={drawerSectionResetSx}>
            <Box className="cart-flyout__items-container" {...styles.itemsContainer}>
              {visibleItemsCount === 0 ? (
                <CartFlyoutEmptyCart isRegistered={isRegistered} />
              ) : isProductsLoading ? (
                <Box
                  aria-busy="true"
                  aria-live="polite"
                  className="cart-flyout__loading-state"
                  {...styles.loadingState}
                >
                  <Text color="gray.600" fontSize="sm">
                    {intl.formatMessage({
                      defaultMessage: 'Loading cart items…',
                      id: 'cart_flyout.loading',
                    })}
                  </Text>
                </Box>
              ) : (
                <CartItemList
                  currency={currency}
                  items={itemsWithLocalQty}
                  outOfStockItemIds={outOfStockItemIds}
                  productsByItemId={productsByItemId}
                  productsByProductId={productsByProductId}
                  promotionsById={promotionsById}
                  removingItemIds={removingItemIds}
                  updatingItemIds={updatingItemIds}
                  onChangeItemQuantity={updateItemQuantity}
                  onEditItem={handleEditItem}
                  onRemoveItem={removeItem}
                />
              )}
            </Box>
          </DrawerBody>
        </Box>
        {/* Footer — sticky pricing summary + checkout */}
        {visibleItemsCount > 0 && (
          <DrawerFooter
            className="cart-flyout__footer"
            {...styles.footer}
            flexDirection="column"
            sx={drawerSectionResetSx}
          >
            <CartPricingSummary totalSavings={totalSavings} />
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};

CartFlyout.displayName = 'CartFlyout';

export default CartFlyout;
